/**
 * The book's pictures, drawn in HTML. Each mirrors a macro in the Level 3 source (packetmacros.tex / lesson1_nines.tex):
 * the Math Map starts at ZERO, even columns are blue, and the rounding line sits between the 4 and 5 columns.
 * All decorative color has a text or shape equivalent, so nothing depends on color alone.
 */

export interface MapOpts {
  rows?: number;
  /** Numbers to ring (circled), e.g. the nines. */
  ring?: number[];
  /** Numbers to fill (the one being asked about). */
  mark?: number[];
  /** Draw the rounding line between the 4 and 5 columns. */
  line?: boolean;
  /** Highlight one column (its top number) or one row (its tens digit). */
  col?: number;
  row?: number;
  caption?: string;
}

/** The Math Map: 0 at the top left, 10 columns, as many rows as asked (10 = 0–99). */
export function mathMap(o: MapOpts = {}): string {
  const rows = o.rows ?? 10;
  const ring = new Set(o.ring ?? []), mark = new Set(o.mark ?? []);
  let h = `<table class="mmap${o.line ? " lined" : ""}" aria-label="${o.caption ? o.caption : `Math Map, 0 to ${rows * 10 - 1}`}"><tbody>`;
  for (let r = 0; r < rows; r++) {
    h += "<tr>";
    for (let c = 0; c < 10; c++) {
      const n = r * 10 + c;
      const cls = [c % 2 === 0 ? "ev" : "od", ring.has(n) ? "ring" : "", mark.has(n) ? "mark" : "", o.col === c ? "hl" : "", o.row === r ? "hl" : "", o.line && c === 4 ? "edge" : ""]
        .filter(Boolean).join(" ");
      h += `<td class="${cls}">${n}</td>`;
    }
    h += "</tr>";
  }
  h += "</tbody></table>";
  if (o.line) h += `<div class="mmap-key"><span>ROUND DOWN · 0 1 2 3 4</span><span>5 6 7 8 9 · ROUND UP</span></div>`;
  return h;
}

/** One row of the map with the rounding line, and the digit in question marked. */
export function roundingLine(digit?: number): string {
  let h = `<table class="mmap lined one" aria-label="The top row of the Math Map with a line between 4 and 5"><tbody><tr>`;
  for (let c = 0; c < 10; c++) h += `<td class="${[c % 2 === 0 ? "ev" : "od", c === 4 ? "edge" : "", digit === c ? "mark" : ""].filter(Boolean).join(" ")}">${c}</td>`;
  h += `</tr><tr><td colspan="5" class="lab">ROUND DOWN</td><td colspan="5" class="lab">ROUND UP</td></tr></tbody></table>`;
  return h;
}

/** The two-square puzzle (lesson1_nines.tex \ninepz): your number's square, the answer's square attached back 1 and down. */
export function ninePuzzle(n: number): string {
  return `<div class="pz9" role="img" aria-label="${n} in a square; the answer square is attached back one and down one row">
    <div class="sq top">${n}</div><div class="sq ans" aria-hidden="true">?</div></div>`;
}

/** A vertical problem the way the book writes it: digits right-aligned, the sign on the bottom line. */
export function vertical(top: number, bottom: number, op: "+" | "−"): string {
  return `<div class="vert" role="img" aria-label="${top} ${op === "+" ? "plus" : "minus"} ${bottom}"><div>${top}</div><div><span class="op">${op}</span>${bottom}</div></div>`;
}

/** A number with one digit underlined (place value). */
export function underlined(n: number, digitIndexFromLeft: number): string {
  const s = String(n);
  return `<span class="big">${s.split("").map((d, i) => (i === digitIndexFromLeft ? `<u>${d}</u>` : d)).join("")}</span>`;
}

/** Hands: `up` fingers up, the rest down. Shapes carry the meaning, not color. */
export function fingers(up: number): string {
  let h = `<div class="hands" role="img" aria-label="${up} fingers up, ${10 - up} down">`;
  for (let i = 0; i < 10; i++) h += `<i class="${i < up ? "up" : "down"}"></i>${i === 4 ? '<b class="gap"></b>' : ""}`;
  return h + `</div>`;
}

/* ---------- Pictures for the check-ups and the later lessons (packetmacros.tex: \clockface, \fracpie, number line, bar graph, \shp) ---------- */

/** An analog clock: numbers 1–12, minute ticks, short hour hand (between numbers as the minutes pass), long minute hand. */
export function clockFace(h: number, m: number, size = 150): string {
  const c = size / 2, r = c - 6;
  let s = `<svg class="clock" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" role="img" aria-label="A clock face"><circle cx="${c}" cy="${c}" r="${r}" fill="var(--bg)" stroke="var(--navy)" stroke-width="2.5"/>`;
  for (let i = 0; i < 60; i++) {
    const a = (i / 60) * 2 * Math.PI, l = i % 5 === 0 ? 8 : 4;
    s += `<line x1="${c + (r - l) * Math.sin(a)}" y1="${c - (r - l) * Math.cos(a)}" x2="${c + r * Math.sin(a)}" y2="${c - r * Math.cos(a)}" stroke="var(--ink)" stroke-width="${i % 5 === 0 ? 2 : 1}"/>`;
  }
  for (let n = 1; n <= 12; n++) {
    const a = (n / 12) * 2 * Math.PI;
    s += `<text x="${c + (r - 20) * Math.sin(a)}" y="${c - (r - 20) * Math.cos(a) + 5}" text-anchor="middle" font-size="${size / 11}" font-weight="700" fill="var(--ink)">${n}</text>`;
  }
  const ha = (((h % 12) + m / 60) / 12) * 2 * Math.PI, ma = (m / 60) * 2 * Math.PI;
  s += `<line x1="${c}" y1="${c}" x2="${c + r * 0.5 * Math.sin(ha)}" y2="${c - r * 0.5 * Math.cos(ha)}" stroke="var(--navy)" stroke-width="5" stroke-linecap="round"/>`;
  s += `<line x1="${c}" y1="${c}" x2="${c + r * 0.82 * Math.sin(ma)}" y2="${c - r * 0.82 * Math.cos(ma)}" stroke="var(--navy)" stroke-width="3" stroke-linecap="round"/><circle cx="${c}" cy="${c}" r="4" fill="var(--navy)"/></svg>`;
  return s;
}

/** A 0-to-1 number line cut into `parts`, with a dot labeled A at `dot` (or none). */
export function numberLine(parts: number, dot?: number, label = "A"): string {
  const w = 320, x0 = 20, x1 = 300, y = 34;
  let s = `<svg width="${w}" height="64" viewBox="0 0 ${w} 64" role="img" aria-label="A number line from 0 to 1 cut into ${parts} equal parts"><line x1="${x0 - 10}" y1="${y}" x2="${x1 + 14}" y2="${y}" stroke="var(--navy)" stroke-width="2"/>`;
  for (let i = 0; i <= parts; i++) {
    const x = x0 + ((x1 - x0) * i) / parts;
    s += `<line x1="${x}" y1="${y - 8}" x2="${x}" y2="${y + 8}" stroke="var(--navy)" stroke-width="2"/>`;
  }
  s += `<text x="${x0}" y="${y + 26}" text-anchor="middle" font-weight="700" fill="var(--ink)">0</text><text x="${x1}" y="${y + 26}" text-anchor="middle" font-weight="700" fill="var(--ink)">1</text>`;
  if (dot !== undefined) {
    const x = x0 + ((x1 - x0) * dot) / parts;
    s += `<circle cx="${x}" cy="${y}" r="6" fill="var(--orange)"/><text x="${x}" y="${y - 14}" text-anchor="middle" font-weight="700" fill="var(--ink)">${label}</text>`;
  }
  return s + `</svg>`;
}

/** A bar graph with gridlines every 1 and labels every 2 (the check-up's graph). Bars carry their label below. */
export function barGraph(bars: { label: string; value: number }[], max = 8, step = 1): string {
  const h = 22, w = 60, left = 30, top = 10, H = max * h;
  let s = `<svg width="${left + bars.length * (w + 20) + 20}" height="${H + top + 34}" role="img" aria-label="Bar graph: ${bars.map((b) => `${b.label} ${b.value}`).join(", ")}">`;
  for (let y = 0; y <= max; y++) {
    const py = top + H - y * h;
    if (y % step) continue;
    s += `<line x1="${left}" y1="${py}" x2="${left + bars.length * (w + 20) + 10}" y2="${py}" stroke="var(--line)"/>`;
    if (y % 2 === 0 || step === 1) s += `<text x="${left - 6}" y="${py + 4}" text-anchor="end" font-size="12" fill="var(--ink)">${y}</text>`;
  }
  bars.forEach((b, i) => {
    const x = left + 14 + i * (w + 20);
    s += `<rect x="${x}" y="${top + H - b.value * h}" width="${w}" height="${b.value * h}" fill="var(--sky)" stroke="var(--navy)" stroke-width="1.5"/>`;
    s += `<text x="${x + w / 2}" y="${top + H + 18}" text-anchor="middle" font-size="12" font-weight="700" fill="var(--ink)">${b.label}</text>`;
  });
  return s + `<line x1="${left}" y1="${top}" x2="${left}" y2="${top + H}" stroke="var(--navy)" stroke-width="2"/><line x1="${left}" y1="${top + H}" x2="${left + bars.length * (w + 20) + 10}" y2="${top + H}" stroke="var(--navy)" stroke-width="2"/></svg>`;
}

const poly = (pts: [number, number][], size = 110) =>
  `<svg width="${size}" height="${size}" viewBox="-1.5 -1.5 3 3" role="img"><polygon points="${pts.map(([x, y]) => `${x},${-y}`).join(" ")}" fill="none" stroke="var(--navy)" stroke-width="0.08" stroke-linejoin="round"/></svg>`;
const regular = (n: number, rot = 90) => Array.from({ length: n }, (_, i) => { const a = ((rot + (360 * i) / n) * Math.PI) / 180; return [1.25 * Math.cos(a), 1.25 * Math.sin(a)] as [number, number]; });
/** The check-ups' shapes (\shp in pretest_3to4.tex). */
export const SHAPES = {
  triangle: poly(regular(3)),
  pentagon: poly(regular(5)),
  hexagon: poly(regular(6, 0)),
  square: poly([[-0.9, -0.9], [0.9, -0.9], [0.9, 0.9], [-0.9, 0.9]]),
  rectangle: poly([[-1.2, -0.7], [1.2, -0.7], [1.2, 0.7], [-1.2, 0.7]]),
  trapezoid: poly([[-1.3, -0.7], [1.3, -0.7], [0.7, 0.7], [-0.7, 0.7]]),
  rhombus: poly([[0, -1.2], [0.8, 0], [0, 1.2], [-0.8, 0]]),
  octagon: poly(regular(8, 22.5)),
  parallelogram: poly([[-1.3, -0.65], [0.6, -0.65], [1.3, 0.65], [-0.6, 0.65]]),
  quadrilateral: poly([[-1.2, -0.8], [1.1, -0.5], [0.5, 1.0], [-0.8, 0.6]]),
  scalene: poly([[-1.3, -0.8], [1.3, -0.8], [-0.4, 1.0]]),
  circle: `<svg width="90" height="90" viewBox="-1.5 -1.5 3 3" role="img"><circle r="1" fill="none" stroke="var(--navy)" stroke-width="0.08"/></svg>`,
};

/** A bar cut into `parts` equal parts with the first `shaded` filled (the check-up's equal-parts figure). */
export function partsBar(parts: number, shaded: number, unit = 34): string {
  let s = `<svg width="${parts * unit + 4}" height="${unit * 1.2 + 4}" role="img" aria-label="A bar cut into ${parts} equal parts, ${shaded} shaded">`;
  for (let i = 0; i < parts; i++) s += `<rect x="${2 + i * unit}" y="2" width="${unit}" height="${unit * 1.2}" fill="${i < shaded ? "var(--sky)" : "var(--bg)"}" stroke="var(--navy)" stroke-width="2"/>`;
  return s + `</svg>`;
}

/** The two-square puzzle for adding 9 or 8 (\ninepz / \numpzd): your number's square, the answer's square attached
 *  back `back` (9's bond 1, 8's bond 2) and down a row. */
export function mapPuzzle(n: number, back: number): string {
  const cols = back + 1;
  return `<div class="pz9" style="grid-template-columns:repeat(${cols},56px)" role="img" aria-label="${n} in a square; the answer square is back ${back} and down one row">
    <div class="sq top" style="grid-column:${cols};grid-row:1">${n}</div><div class="sq ans" style="grid-column:1;grid-row:2" aria-hidden="true">?</div></div>`;
}

/** A dot array: `cols` columns with `per` dots in each (we count arrays by columns). */
export function dotArray(cols: number, per: number): string {
  const g = 20;
  let s = `<svg width="${cols * g + 10}" height="${per * g + 10}" role="img" aria-label="${cols} columns of ${per} dots">`;
  for (let c = 0; c < cols; c++) for (let r = 0; r < per; r++) s += `<circle cx="${10 + c * g}" cy="${10 + r * g}" r="6" fill="var(--navy)"/>`;
  return s + `</svg>`;
}

/** Subtracting 9 or 8 on the Map: your number's square, the answer's square forward `fwd` and UP a row (L2.20). */
export function mapPuzzleUp(n: number, fwd: number): string {
  const cols = fwd + 1;
  return `<div class="pz9" style="grid-template-columns:repeat(${cols},56px)" role="img" aria-label="${n} in a square; the answer square is forward ${fwd} and up one row">
    <div class="sq ans" style="grid-column:${cols};grid-row:1" aria-hidden="true">?</div><div class="sq top" style="grid-column:1;grid-row:2">${n}</div></div>`;
}

/** A plain keypad (7 8 9 / 4 5 6 / 1 2 3 / 0) with one key ringed — for adding and subtracting 7. */
export function keypadRing(ring?: number): string {
  const rows = [[7, 8, 9], [4, 5, 6], [1, 2, 3]];
  let h = `<div class="kp7" role="img" aria-label="Keypad${ring !== undefined ? ` with the ${ring} ringed` : ""}">`;
  for (const r of rows) for (const d of r) h += `<span class="${d === ring ? "on" : ""}">${d}</span>`;
  return h + `<span class="z${ring === 0 ? " on" : ""}">0</span></div>`;
}

/** A circle cut into `parts` equal slices, the first `shaded` filled. */
export function fracPie(parts: number, shaded: number, size = 120): string {
  const c = size / 2, r = c - 4;
  let s = `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" role="img" aria-label="A circle cut into ${parts} equal parts, ${shaded} shaded">`;
  if (parts === 1) return s + `<circle cx="${c}" cy="${c}" r="${r}" fill="${shaded ? "var(--sky)" : "var(--bg)"}" stroke="var(--navy)" stroke-width="2"/></svg>`;
  for (let i = 0; i < parts; i++) {
    const a0 = (i / parts) * 2 * Math.PI - Math.PI / 2, a1 = ((i + 1) / parts) * 2 * Math.PI - Math.PI / 2;
    s += `<path d="M${c},${c} L${c + r * Math.cos(a0)},${c + r * Math.sin(a0)} A${r},${r} 0 ${a1 - a0 > Math.PI ? 1 : 0} 1 ${c + r * Math.cos(a1)},${c + r * Math.sin(a1)} Z" fill="${i < shaded ? "var(--sky)" : "var(--bg)"}" stroke="var(--navy)" stroke-width="2"/>`;
  }
  return s + `</svg>`;
}
/** A bar cut into pieces of the given widths (to show parts that are NOT equal). */
export function cutBar(widths: number[], shaded: number[] = [], unit = 34): string {
  const total = widths.reduce((a, b) => a + b, 0);
  let s = `<svg width="${total * unit + 4}" height="${unit * 1.2 + 4}" role="img" aria-label="A bar cut into ${widths.length} parts${new Set(widths).size > 1 ? " that are not the same size" : ""}">`;
  let x = 2;
  widths.forEach((w, i) => { s += `<rect x="${x}" y="2" width="${w * unit}" height="${unit * 1.2}" fill="${shaded.includes(i) ? "var(--sky)" : "var(--bg)"}" stroke="var(--navy)" stroke-width="2"/>`; x += w * unit; });
  return s + `</svg>`;
}
/** Pictures side by side, each with an optional caption under it. */
export function sideBySide(pics: { svg: string; cap?: string }[]): string {
  return `<div class="sbs">${pics.map((p) => `<figure>${p.svg}${p.cap ? `<figcaption>${p.cap}</figcaption>` : ""}</figure>`).join("")}</div>`;
}
/** A fraction written top over bottom, for prompts. */
export const fr = (a: number | string, b: number | string): string => `<span class="frac"><span>${a}</span><span>${b}</span></span>`;

/** A rectangle tiled with square units: `cols` columns, `rows` rows. */
export function sqGrid(cols: number, rows: number, unit = 26): string {
  let s = `<svg width="${cols * unit + 4}" height="${rows * unit + 4}" role="img" aria-label="A rectangle tiled with square units, ${cols} columns of ${rows}">`;
  for (let c = 0; c < cols; c++) for (let r = 0; r < rows; r++) s += `<rect x="${2 + c * unit}" y="${2 + r * unit}" width="${unit}" height="${unit}" fill="var(--sky)" stroke="var(--navy)" stroke-width="1.5"/>`;
  return s + `</svg>`;
}
/** A rectangle with its side lengths written on it. `all` labels all four sides; otherwise just the top and the left. */
export function rectLabeled(l: number, w: number, unit = "", all = true): string {
  const sc = Math.min(22, 220 / Math.max(l, w * 1.6)), W = l * sc, H = w * sc, x0 = 40, y0 = 26;
  const u = unit ? ` ${unit}` : "";
  let s = `<svg width="${W + 90}" height="${H + 56}" role="img" aria-label="A rectangle ${l}${u} long and ${w}${u} wide"><rect x="${x0}" y="${y0}" width="${W}" height="${H}" fill="var(--bg)" stroke="var(--navy)" stroke-width="2.5"/>`;
  const t = (x: number, y: number, v: string, a = "middle") => `<text x="${x}" y="${y}" text-anchor="${a}" font-weight="700" fill="var(--ink)">${v}</text>`;
  s += t(x0 + W / 2, y0 - 8, `${l}${u}`) + t(x0 - 8, y0 + H / 2 + 5, `${w}${u}`, "end");
  if (all) s += t(x0 + W / 2, y0 + H + 20, `${l}${u}`) + t(x0 + W + 8, y0 + H / 2 + 5, `${w}${u}`, "start");
  return s + `</svg>`;
}
/** A square cut in half: down the middle, across, or corner to corner; one half shaded. */
export function squareHalf(kind: "v" | "h" | "d", size = 96): string {
  const z = size - 4;
  const half = kind === "v" ? `M2,2 H${2 + z / 2} V${2 + z} H2 Z` : kind === "h" ? `M2,2 H${2 + z} V${2 + z / 2} H2 Z` : `M2,2 H${2 + z} L2,${2 + z} Z`;
  const cut = kind === "v" ? `M${2 + z / 2},2 V${2 + z}` : kind === "h" ? `M2,${2 + z / 2} H${2 + z}` : `M${2 + z},2 L2,${2 + z}`;
  return `<svg width="${size}" height="${size}" role="img" aria-label="A square cut into two equal halves, one shaded"><path d="${half}" fill="var(--sky)"/><rect x="2" y="2" width="${z}" height="${z}" fill="none" stroke="var(--navy)" stroke-width="2"/><path d="${cut}" stroke="var(--navy)" stroke-width="2"/></svg>`;
}
